import { useRef, useCallback, useEffect } from 'react';

/**
 * Custom hook to handle mobile keyboard behavior for input fields
 *
 * Features:
 * - Auto-scrolls focused inputs into view above the keyboard
 * - Dismisses keyboard when tapping outside input fields
 * - Smooth scroll animation
 *
 * @param {Object} options - Configuration options
 * @param {number} options.scrollPadding - Pixels of padding above input (default: 100)
 * @param {boolean} options.dismissOnOutsideClick - Enable tap-to-dismiss (default: true)
 * @param {boolean} options.smoothScroll - Use smooth scrolling (default: true)
 * @returns {Object} { inputProps, containerRef }
 */
export const useInputKeyboardHandler = ({
  scrollPadding = 100,
  dismissOnOutsideClick = true,
  smoothScroll = true
} = {}) => {
  const containerRef = useRef(null);
  const inputRefs = useRef(new Set());

  // Handle input focus - scroll into view above keyboard
  const handleFocus = useCallback((event) => {
    const input = event.target;

    // Small delay to ensure keyboard is showing
    setTimeout(() => {
      const rect = input.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const visualViewport = window.visualViewport;

      // Use visual viewport if available (better for iOS)
      const viewportHeight = visualViewport ? visualViewport.height : windowHeight;

      // Check if input is covered by keyboard
      if (rect.bottom > viewportHeight - 50) {
        // For iOS Safari/Chrome, use scrollIntoView with center alignment
        input.scrollIntoView({
          behavior: smoothScroll ? 'smooth' : 'auto',
          block: 'center',
          inline: 'nearest'
        });

        // Additional scroll adjustment for padding
        setTimeout(() => {
          const newRect = input.getBoundingClientRect();
          if (newRect.bottom > viewportHeight - 50) {
            window.scrollBy({
              top: newRect.top - scrollPadding,
              behavior: smoothScroll ? 'smooth' : 'auto'
            });
          }
        }, smoothScroll ? 150 : 50);
      }
    }, 150);
  }, [scrollPadding, smoothScroll]);

  // Handle outside click - dismiss keyboard
  const handleOutsideClick = useCallback((event) => {
    if (!dismissOnOutsideClick) return;

    const target = event.target;
    const tagName = target.tagName;

    // Check if clicked element is an input, textarea, select, button, or label
    const isInteractiveElement =
      tagName === 'INPUT' ||
      tagName === 'TEXTAREA' ||
      tagName === 'SELECT' ||
      tagName === 'BUTTON' ||
      tagName === 'LABEL' ||
      target.closest('button') !== null ||
      target.closest('label') !== null;

    // If clicked on a non-interactive element, blur the active input
    if (!isInteractiveElement && document.activeElement) {
      const activeEl = document.activeElement;
      if (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || activeEl.tagName === 'SELECT') {
        activeEl.blur();
      }
    }
  }, [dismissOnOutsideClick]);

  // Setup event listeners
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Add click/touch listeners for dismiss-on-outside-click
    if (dismissOnOutsideClick) {
      container.addEventListener('click', handleOutsideClick);
      container.addEventListener('touchstart', handleOutsideClick);
    }

    return () => {
      if (container) {
        container.removeEventListener('click', handleOutsideClick);
        container.removeEventListener('touchstart', handleOutsideClick);
      }
    };
  }, [dismissOnOutsideClick, handleOutsideClick]);

  // Register an input element
  const registerInput = useCallback((inputElement) => {
    if (inputElement) {
      inputRefs.current.add(inputElement);
      return () => {
        inputRefs.current.delete(inputElement);
      };
    }
  }, []);

  // Props to spread on input elements
  const inputProps = {
    onFocus: handleFocus
  };

  return {
    inputProps,
    containerRef,
    registerInput
  };
};
